/**
 * FiasAIArcheSuggestions — arche buttons under an assistant reply, the
 * Make-a-Request suggestion row's compact sibling for the floating
 * window. Suggestions come from the agent's arche refs (search results /
 * recommendations); clicking navigates to the arche (window stays open).
 */

import { ArrowRight } from 'lucide-react';
import type { AgentChatMessage, AgentEvent } from './agent-types';

export interface FiasAIArcheSuggestion {
  id: string;
  label: string;
  description?: string;
  imageUrl?: string | null;
}

/**
 * Restored messages carry persisted `archeSuggestions`; live turns derive
 * from the tool events' arche refs (same rule as the MaR page).
 */
export function deriveArcheSuggestions(msg: AgentChatMessage): FiasAIArcheSuggestion[] {
  if (msg.archeSuggestions && msg.archeSuggestions.length > 0) {
    return msg.archeSuggestions.map((s) => ({
      id: s.archId,
      label: s.name,
      description: s.simpleDescription,
      imageUrl: s.imageRef,
    }));
  }
  const out: FiasAIArcheSuggestion[] = [];
  const seen = new Set<string>();
  for (const event of msg.events ?? []) {
    if (event.kind !== 'tool') continue;
    for (const ref of (event as Extract<AgentEvent, { kind: 'tool' }>).refs ?? []) {
      if (ref.type === 'arche' && !seen.has(ref.id)) {
        seen.add(ref.id);
        out.push({
          id: ref.id,
          label: ref.label,
          description: (ref as { description?: string }).description,
          imageUrl: (ref as { imageUrl?: string | null }).imageUrl,
        });
      }
    }
  }
  return out;
}

export function FiasAIArcheSuggestions({
  suggestions,
  onOpen,
}: {
  suggestions: FiasAIArcheSuggestion[];
  onOpen: (archeId: string) => void;
}) {
  if (suggestions.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-1.5" data-testid="fias-ai-arche-suggestions">
      {suggestions.slice(0, 3).map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onOpen(s.id)}
          className="group flex items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-2 text-left text-sm transition-all hover:border-primary/50 hover:bg-accent/50"
        >
          {s.imageUrl ? (
            <img
              src={s.imageUrl}
              alt=""
              className="h-7 w-7 flex-shrink-0 rounded-md object-cover"
            />
          ) : (
            <div className="h-7 w-7 flex-shrink-0 rounded-md bg-primary/10" />
          )}
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{s.label}</span>
            {s.description && (
              <span className="block truncate text-xs text-muted-foreground">{s.description}</span>
            )}
          </span>
          <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </button>
      ))}
    </div>
  );
}
